/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { createPortal } from 'react-dom'
import { View } from '@components/atoms/views/View'
import { Pressable } from '@components/atoms/button/Pressable'
import { Icon } from '@components/atoms/icons/Icon'
import { BackofficeTheme } from './BackofficeTheme'
import { mono, boFont, boMobile, boZ, boShared, boMedia } from './backoffice.tokens'
import { useBoSizeClass } from './useBoSizeClass'
import { BoSheet } from './BoSheet'

interface PropsType {
	open: boolean
	onClose: () => void
	title: string
	/** 상단 바 우측 동작 — 최대 2개 */
	actions?: React.ReactNode
	children?: React.ReactNode
	/** 하단 sticky 동작 바 */
	bottomBar?: React.ReactNode
	/** 폰이 아닐 때 시트로 열지(편집 폼) · 전체 화면으로 열지(자세히). 기본 sheet */
	wideMode?: 'sheet' | 'full'
	className?: string
}

/**
 * 스택 한 단 — 목록 위에 덮이는 전폭 페이지 (RESPONSIVE_PLAN §4.1·§7).
 * 폰: fixed 전폭, 상단 바 48(‹ · 제목 · 동작), 본문 스크롤러 하나, 하단 sticky 동작 바. 슬라이드 200ms.
 * 폰이 아니면 BoSheet(사이드 시트/Dialog) 또는 전체 화면(자세히)으로 같은 내용을 연다.
 * 열림/닫힘 정본은 부모가 쥔 URL 이다 — 이 컴포넌트는 open 만 본다.
 */
export const BoStack = (props: PropsType) => {
	const size = useBoSizeClass()
	const wideMode = props.wideMode ?? 'sheet'

	React.useEffect(() => {
		if (!props.open) return
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') props.onClose()
		}
		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
	}, [props.open, props.onClose])

	if (size !== 'phone' && wideMode === 'sheet') {
		return (
			<BoSheet
				open={props.open}
				onClose={props.onClose}
				title={props.title}
				footer={props.bottomBar}
				className={props.className}
			>
				{props.actions ? (
					<View className="boStackSheetActions">{props.actions}</View>
				) : null}
				{props.children}
			</BoSheet>
		)
	}

	if (!props.open) return null

	const node = (
		<BackofficeTheme>
			<View
				css={style}
				className={`boStack ${size} ${props.className ?? ''}`}
				role="dialog"
				aria-modal
			>
				<View className="boStackTop">
					<Pressable className="boStackBack" onClick={props.onClose} aria-label="뒤로">
						<Icon name="chevron-left" className="boStackBackIcon" />
					</Pressable>
					<span className="boStackTitle">{props.title}</span>
					<View className="boStackActions">{props.actions ?? null}</View>
				</View>
				<View className="boStackBody">{props.children}</View>
				{props.bottomBar ? (
					<View className="boStackBottom">{props.bottomBar}</View>
				) : null}
			</View>
		</BackofficeTheme>
	)

	return createPortal(node, document.body)
}

const style = css({
	...boShared,
	position: 'fixed',
	inset: 0,
	zIndex: boZ.stack,
	display: 'flex',
	flexDirection: 'column',
	// dvh 폴백 — 앞 값이 못 읽히면 뒤 값 (RESPONSIVE_PLAN §11)
	height: ['100vh', '100dvh'],
	background: mono.bg,
	fontFamily: boFont.ui,
	color: mono.ink,
	animation: 'boStackIn 200ms ease-out',
	'@keyframes boStackIn': {
		from: { transform: 'translateX(24px)', opacity: 0 },
		to: { transform: 'translateX(0)', opacity: 1 },
	},
	'@media (prefers-reduced-motion: reduce)': { animation: 'none' },

	'& .text': { fontFamily: boFont.ui },

	'.boStackTop': {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		gap: 4,
		height: boMobile.topBarH,
		paddingTop: 'env(safe-area-inset-top, 0px)',
		boxSizing: 'content-box',
		padding: '0 8px 0 4px',
		background: mono.bg,
		borderBottom: `1px solid ${mono.line}`,

		'.boStackBack': {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			width: 44,
			height: 44,
			flexShrink: 0,
			'.boStackBackIcon': { fontSize: 26, color: mono.ink },
		},
		'.boStackTitle': {
			flex: 1,
			minWidth: 0,
			fontSize: 16,
			fontWeight: 500,
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},
		'.boStackActions': {
			display: 'flex',
			alignItems: 'center',
			gap: 4,
			flexShrink: 0,
			'& > *': { minHeight: 44 },
		},
	},

	'.boStackBody': {
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
		overscrollBehaviorY: 'contain',
		overscrollBehaviorX: 'auto',
		WebkitOverflowScrolling: 'touch',
		padding: '0 16px 24px',
	},

	'.boStackBottom': {
		flexShrink: 0,
		display: 'flex',
		gap: 8,
		padding: '10px 16px',
		paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
		borderTop: `1px solid ${mono.line}`,
		background: mono.surface,
		'& > *': { flex: 1 },
	},

	'&.tablet': {
		[boMedia.tablet]: {
			'.boStackBody': { padding: '0 24px 32px' },
		},
	},
})
